"use client";

import { useMemo, useRef } from "react";
import { useCanvasDrag } from "@/lib/canvas-drag";
import { getDefinition } from "@/lib/catalog";
import {
  runAllDiagnostics,
  type BatterySoc,
  type ComponentStates,
  type LoadStates,
} from "@/lib/diagnostics";
import type { System } from "@/lib/types";

/** Position/waypoint gestures do not change electrical checks. Keep the last
 * result during the gesture and refresh from the final drawing on release. */
export function useSystemDiagnostics(
  system: System,
  componentStates?: ComponentStates,
  batterySoc?: BatterySoc,
  autoSizeTargetPct?: number,
  loadStates?: LoadStates,
) {
  const isDragging = useCanvasDrag((s) => s.isDragging);
  const lastRef = useRef<ReturnType<typeof runAllDiagnostics> | null>(null);
  return useMemo(() => {
    if (isDragging && lastRef.current) return lastRef.current;
    const result = runAllDiagnostics(
      system, getDefinition, componentStates, batterySoc, autoSizeTargetPct, loadStates,
    );
    lastRef.current = result;
    return result;
  }, [system, componentStates, batterySoc, autoSizeTargetPct, loadStates, isDragging]);
}
